// components/Cart.js
import { useCart } from "../contexts/CartContext.jsx";
import { Link, useNavigate } from "react-router-dom";
import "../styles/cart.css";
import { useState } from "react";

const Cart = () => {
  const {
    cartItems,
    cartTotal,
    removeFromCart,
    updateQuantity,
    clearCart,
    itemCount,
  } = useCart();
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const navigate = useNavigate();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const calculateTotal = () => {
    const subtotal = cartTotal;
    const deliveryFee = includeDelivery ? subtotal * 0.05 : 0; // 5% de frais de livraison
    return subtotal + deliveryFee;
  };

  //token validation function
  const isTokenValid = (token) => {
    if (!token) return false;

    try {
      // Basic check: should have 3 parts separated by dots
      const parts = token.split(".");
      if (parts.length !== 3) {
        console.log("Invalid token structure");
        return false;
      }

      if (token.length < 10) {
        // JWT tokens are usually longer
        console.log("Token too short");
        return false;
      }

      return true;
    } catch (error) {
      console.log("Invalid token format:", error);
      return false;
    }
  };

  const refreshAuthState = () => {
    localStorage.removeItem("clientToken");
    localStorage.removeItem("userData");
    navigate("/login");
  };

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    try {
      const userData = localStorage.getItem("userData");
      const token = localStorage.getItem("clientToken");

      /*console.log("=== CHECKOUT DEBUG INFO ===");
      console.log("Token exists:", !!token);
      console.log("Token length:", token?.length);
      console.log("Token preview:", token?.substring(0, 50) + "...");
      console.log("User data:", JSON.parse(userData));*/

      if (!userData) {
        console.log("No user data found");
        navigate("/login");
        return;
      }

      if (!token) {
        console.log("No token found");
        alert("Please login again");
        navigate("/login");
        return;
      }

      // Validate token format and expiration
      if (!isTokenValid(token)) {
        console.log("Token is invalid or expired");
        alert("Session expired. Please login again");
        refreshAuthState();
        return;
      }

      const user = JSON.parse(userData);
      //console.log("User ID:", user.id);
      //console.log("Cart items count:", cartItems.length);

      // Validate cart items
      if (!cartItems || cartItems.length === 0) {
        alert("Your cart is empty");
        return;
      }

      const orderData = {
        items: cartItems.map((item) => ({
          id: item.id,
          name: item.name,
          stock: item.stock,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          image1: item.image1,
          sellercode: item.sellercode || "default",
        })),
        includeDelivery,
        clientId: user.id,
        paymentMethod: paymentMethod || "cash",
      };

      console.log("Order data being sent:", JSON.stringify(orderData, null, 2));

      // a simple request to Test token
      console.log("Testing token validity...");
      const testResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/orders/test`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Test response status:", testResponse.status);

      if (!testResponse.ok) {
        const testError = await testResponse.text();
        console.log("Test request failed:", testError);

        if (testResponse.status === 401) {
          alert("Authentication failed. Please login again");
          refreshAuthState();
          navigate("/login");
          return;
        }
      }

      ///////////////

      console.log("Creating order...");
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/orders`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData),
        }
      );

      //console.log("Order response status:", response.status);
      //console.log("Order response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Order creation failed:", errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }

        if (response.status === 401) {
          alert("Session expired. Please login again");
          refreshAuthState();
          return;
        }

        throw new Error(
          errorData.message || `Server error: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Order created successfully:", result);

      clearCart();
      navigate("/order-confirmation", {
        state: {
          orderId: result.orderId || result.id,
          orderCode: result.orderCode,
          total: result.totalPrice || calculateTotal(),
          paymentMethod: paymentMethod,
        },
      });
    } catch (error) {
      console.error("Checkout error:", error);
      alert(`Checkout failed: ${error.message}`);
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="cart-container">
      <h2>Your Cart ({itemCount} items)</h2>
      {cartItems.length === 0 ? (
        <p>Your cart is empty</p>
      ) : (
        <>
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <img
                  src={`${import.meta.env.VITE_API_URL}/${item.image1}`}
                  alt={item.name}
                  className="cart-item-image"
                />
                <div className="cart-item-details">
                  <h3>{item.name}</h3>
                  <h3>Stock: {item.stock}</h3>
                  <p>Price: {item.price.toLocaleString()} Fbu</p>
                  <div className="quantity-control">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      disabled={item.quantity >= item.stock}
                    >
                      +
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="remove-btn"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h3>Order Summary</h3>
            <div className="chekcartpart">
              <div className="delivery-option">
                <label>
                  <input
                    type="checkbox"
                    checked={includeDelivery}
                    onChange={(e) => setIncludeDelivery(e.target.checked)}
                  />
                  Include Delivery (+5%)
                </label>
              </div>
              <div className="payment-method">
                <label>Payment Method:</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="payment-select"
                >
                  <option value="">Select payment method</option>
                  <option value="cash">Cash on Delivery</option>
                  <option value="lumicash">Lumicash</option>
                  <option value="bancobu">Bancobu</option>
                  <option value="bancobu-enoti">Bancobu Enoti</option>
                  <option value="coopec">COOPEC</option>
                  <option value="iHela"> iHela</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              <div className="price-breakdown">
                <p>Subtotal: {cartTotal.toLocaleString()} Fbu</p>
                {includeDelivery && (
                  <p>Delivery Fee: {(cartTotal * 0.05).toLocaleString()} Fbu</p>
                )}
                <p className="total-price">
                  Total: {calculateTotal().toLocaleString()} Fbu
                </p>
              </div>
            </div>
            <button
              onClick={handleCheckout}
              disabled={isCheckingOut || !paymentMethod}
              className="order-btn"
            >
              {isCheckingOut ? "Processing..." : "Proceed to Checkout"}
            </button>{" "}
            <div>
              <Link to="/market" className="btn2">
                Continue Shopping
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
